import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { topicAPI, progressAPI } from '../api/client';
import { resolveFontSize } from '../config/fontSizes';
import { useLanguage } from '../context/LanguageContext';
import { useLevel } from '../context/LevelContext';
import { slugifyLessonTitle } from '../utils/lessonSlug';
import { appLangToUrlLang, buildLevelPath, urlLangToAppLang } from '../utils/routeLanguage';
import { Lock, ChevronLeft, ChevronRight, Search } from 'lucide-react';

export const StudentLevelPage = () => {
  const { lang, level: routeLevel } = useParams();
  const { t, language, setLanguage } = useLanguage();
  const { selectedLevel, changeLevel, levels } = useLevel();
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualUpdatingId, setManualUpdatingId] = useState(null);

  useEffect(() => {
    const nextLanguage = urlLangToAppLang(lang);
    if ((nextLanguage === 'ru' || nextLanguage === 'uk') && nextLanguage !== language) {
      setLanguage(nextLanguage);
    }
  }, [lang, language, setLanguage]);

  useEffect(() => {
    const normalizedRouteLevel = String(routeLevel || '').toUpperCase();
    if (levels.includes(normalizedRouteLevel) && normalizedRouteLevel !== selectedLevel) {
      changeLevel(normalizedRouteLevel);
    }
  }, [routeLevel, levels, selectedLevel, changeLevel]);

  useEffect(() => {
    const normalizedRouteLevel = String(routeLevel || '').toUpperCase();
    if (!levels.includes(normalizedRouteLevel) || normalizedRouteLevel !== selectedLevel) {
      navigate(buildLevelPath(language, selectedLevel), { replace: true });
    }
  }, [routeLevel, selectedLevel, language, levels, navigate]);

  useEffect(() => {
    fetchTopics();
  }, [selectedLevel, language]);

  useEffect(() => {
    const handler = () => fetchTopics();
    window.addEventListener('topicsUpdated', handler);
    return () => window.removeEventListener('topicsUpdated', handler);
  }, []);

  const fetchTopics = async () => {
    try {
      const response = await topicAPI.getAllTopics({ language, includeExercises: false });
      const filteredTopics = response.data.filter(topic => topic.level === selectedLevel);
      setTopics(filteredTopics);

      const progressResponse = await progressAPI.getAllProgress();
      const progressMap = {};
      progressResponse.data.forEach((p) => {
        progressMap[p.topicId] = p;
      });
      setProgress(progressMap);
    } catch (error) {
      console.error('Error fetching topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const canAccessTopic = () => true;

  const handleManualToggle = async (e, topicId, currentManual) => {
    e.stopPropagation();
    try {
      setManualUpdatingId(topicId);
      const response = await progressAPI.setManualCompletion(topicId, !currentManual);
      setProgress((prev) => ({
        ...prev,
        [topicId]: response.data
      }));
    } catch (error) {
      console.error('Error updating manual completion:', error);
      const errorMessage = error.response?.data?.error || error.message || '';
      if (errorMessage) alert(errorMessage);
    } finally {
      setManualUpdatingId(null);
    }
  };

  const handleTopicClick = (e, topic, hasAccess) => {
    if (e.target.closest('button')) return;
    if (hasAccess) {
      const title = topic.germanTitle || topic.title || '';
      const slug = slugifyLessonTitle(title);
      navigate(`/${appLangToUrlLang(language)}/${topic.level}/${slug}`);
    }
  };

  const getLevelClass = (level) => {
    const map = { 'A1': 'lv-a1', 'A2': 'lv-a2', 'B1': 'lv-b1', 'B2': 'lv-b2', 'C1': 'lv-c1', 'C2': 'lv-c2' };
    return map[level] || 'lv-a1';
  };

  if (loading) {
    return (
      <div className="modern-page-container student-level-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', animation: 'pulse 2s infinite' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📚</div>
          <div style={{ color: 'var(--gold)', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '12px' }}>
            {t('loading')}
          </div>
        </div>
      </div>
    );
  }

  const filteredTopics = topics.filter(topic =>
    topic.germanTitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
    topic.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handlePreviousLevel = () => {
    const idx = levels.indexOf(selectedLevel);
    if (idx > 0) {
      const nextLevel = levels[idx - 1];
      changeLevel(nextLevel);
      navigate(buildLevelPath(language, nextLevel));
    }
  };

  const handleNextLevel = () => {
    const idx = levels.indexOf(selectedLevel);
    if (idx < levels.length - 1) {
      const nextLevel = levels[idx + 1];
      changeLevel(nextLevel);
      navigate(buildLevelPath(language, nextLevel));
    }
  };

  return (
    <div className="modern-page-container student-level-page">
      <div className="student-hero">
        <h1 className="student-hero-title">
          {language === 'uk'
            ? (<>
                Lingua<span className="accent">DE</span> — короткий шлях <span className="accent">до німецької</span>
              </>)
            : (<>
                Lingua<span className="accent">DE</span> — короткий путь <span className="accent">к немецкому</span>
              </>)}
        </h1>
        <p className="student-hero-sub">{t('masterGermanInteractive')}</p>

        <div className="student-level-selector">
          <button
            onClick={handlePreviousLevel}
            disabled={levels.indexOf(selectedLevel) === 0}
            className="student-level-nav-btn"
          >
            <ChevronLeft size={18} />
          </button>
          <div className={`student-level-badge ${getLevelClass(selectedLevel)}`}>
            <span className="student-level-code">{selectedLevel}</span>
          </div>
          <button
            onClick={handleNextLevel}
            disabled={levels.indexOf(selectedLevel) === levels.length - 1}
            className="student-level-nav-btn"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      <div className="student-divider" />

      <div className="student-content">
        <div className="student-section-label">{t('searchLessonsSection')}</div>

        <div className="student-search-wrap">
          <Search size={16} className="student-search-icon" />
          <input
            type="text"
            placeholder=""
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="student-search-input"
          />
        </div>

        {filteredTopics.length === 0 ? (
          <div className="student-empty-state">
            <span className="student-empty-icon">📚</span>
            <div className="student-empty-title">
              {searchQuery ? t('lessonsNotFound') : `${t('noTopicsForLevel')} ${selectedLevel}`}
            </div>
            <div className="student-empty-desc">
              {searchQuery ? t('tryChangeQuery') : t('selectOtherLevel')}
            </div>
          </div>
        ) : (
          <div className="student-topics-grid">
            {filteredTopics.map((topic, index) => {
              const hasAccess = canAccessTopic(index);
              const topicProgress = progress[topic.id];
              const manualCompleted = Boolean(topicProgress?.manualCompleted);
              const isComplete = manualCompleted || topicProgress?.progress >= 95;
              const progressPercent = topicProgress?.progress || 0;

              return (
                <div
                  key={topic.id}
                  onClick={(e) => handleTopicClick(e, topic, hasAccess)}
                  className={`student-topic-card student-animate-in${isComplete ? ' completed' : ''}${!hasAccess ? ' locked' : ''}`}
                >
                  <div className="student-card-header">
                    <button
                      onClick={(e) => handleManualToggle(e, topic.id, manualCompleted)}
                      disabled={manualUpdatingId === topic.id}
                      className={`student-card-toggle ${manualCompleted ? 'active' : 'inactive'}`}
                    >
                      {manualCompleted ? t('markNotCompleted') : t('markCompleted')}
                    </button>
                    {isComplete && (
                      <div className="student-card-status done">✓</div>
                    )}
                    {!hasAccess && (
                      <div className="student-card-status locked-icon">
                        <Lock size={14} />
                      </div>
                    )}
                  </div>

                  {(() => {
                    const displayTitle = (language === 'de' || !topic.title) ? (topic.germanTitle || topic.title) : topic.title || topic.germanTitle;
                    const displayDesc = (language === 'de' || !topic.description) ? (topic.germanDescription || topic.description) : topic.description || topic.germanDescription;
                    return (
                      <>
                        <h3
                          className={`student-card-title ${topic.titleFontSize || ''}`}
                          style={{ color: topic.titleColor || 'var(--white)', ...(resolveFontSize(topic.titleFontSize) ? { fontSize: resolveFontSize(topic.titleFontSize) } : {}) }}
                        >
                          {displayTitle}
                        </h3>
                        <p className="student-card-subtitle">{displayDesc}</p>
                      </>
                    );
                  })()}

                  <div className="student-progress-wrap">
                    <div className="student-progress-bar-outer">
                      <div
                        className="student-progress-bar-inner"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="student-card-footer">
                    <span className="student-card-level-tag">{topic.level}</span>
                    <span className="student-card-exercise-count">
                      {Number(topic.exerciseCount) || topic.exercises?.length || 0} {t('exercises')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};